import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import * as Sentry from '@sentry/react';
// Provider-neutral seam (US-008). Which adapter answers — keycloak-js or the
// Scalekit PKCE adapter — is decided by VITE_AUTH_PROVIDER inside
// `./authProvider`, which defaults to keycloak. Nothing below knows or cares.
import {
  beginFederatedLogin,
  beginLogin,
  endSession,
  getAccessToken,
  getSubject,
  isRecoveryRedirectPending,
  recoverSession,
  resolveAuthProviderName,
  startSession,
  subscribeToSessionEvents,
  type AuthProviderName,
} from './authProvider';
import { cleanCurrentUrl, hasAuthorizationResponse } from './browserSession';
import {
  discardPendingTransaction as discardScalekitTransaction,
  hasPendingTransaction as scalekitHasPendingTransaction,
} from './scalekit';
// Read DIRECTLY rather than through `resolveAuthProviderName`: a null snapshot
// is "the backend never answered", and the provider name collapses that into
// the same `'keycloak'` a served answer produces. The callback path is the one
// place that distinction decides whether a single-use code gets burnt.
import { getRuntimeAuthConfig } from './runtimeAuthConfig';
import { apiRequestJson, ApiRequestError } from '../api';
import { loadRuntimeAuthConfig } from '../api/publicConfig';
import { isTransientAuthError } from './authErrors';
import { enableSentryReplay, setSentryUser } from '../sentry';

export type CurrentUserProfile = {
  subject: string;
  email: string | null;
  displayName: string | null;
  admin: boolean;
  roles: string[];
  accessibleBrands: Array<{
    id: string;
    name: string;
    url: string;
    /** Authoritative backend decision for approval actions on this brand. */
    canApprove: boolean;
  }>;
  /**
   * The user's preferred IANA timezone (e.g. `'America/New_York'`).
   *
   * Returned by `GET /api/auth/me` (LUT-216). Null when the user hasn't set a
   * preference — UI defaults to UTC in that case. The backend may omit the
   * field entirely from the JSON response when null, so `refreshProfile`
   * normalises an absent field to `null`.
   */
  timezone: string | null;
  /**
   * Custom-terms login gate (set only for a designated signer who owes a
   * signature). When true, the app hard-blocks on the acceptance page until the
   * signer accepts; the three `requiredTerms*` fields name what to sign. The
   * backend omits the string fields when null, so they're normalised here.
   */
  termsAcceptanceRequired: boolean;
  requiredTermsBrandId: string | null;
  requiredTermsVariantKey: string | null;
  requiredTermsVersion: string | null;
  /**
   * Partner inline page-editor pilot flag — mirrors the backend
   * `partner.editor.enabled` config, surfaced on `GET /api/auth/me`
   * (`CurrentUserResponse#partnerEditorEnabled`). Gates the per-candidate
   * "Edit" action in the Approval Pipeline (spec §5.6). Optional on the wire
   * (the backend omits it when false / older responses predate it) and
   * normalised to `false` in {@link AuthProvider.refreshProfile}; default off
   * until the pilot is opened.
   */
  partnerEditorEnabled?: boolean;
  /**
   * Owner-only preview of the decision-first reporting canvas. The backend
   * derives this capability from the verified token identity; clients must
   * fail closed when an older response omits it.
   */
  reportingBetaEnabled?: boolean;
};

type AuthContextValue = {
  loading: boolean;
  authenticated: boolean;
  user: CurrentUserProfile | null;
  loginWithGoogle: (redirectPath?: string) => Promise<void>;
  loginWithPassword: (redirectPath?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * How long boot will wait on the RETRIED runtime-config fetch before giving up
 * on it. Only ever reached on the Scalekit-callback path, and only after the
 * first fetch already failed.
 *
 * `requestJson` sets no timeout and no AbortSignal, so without this a backend
 * that accepts the connection and never answers would hang the boot spinner
 * indefinitely — the failure 5f4946622 removed from the first fetch, and the
 * one `scalekit.ts`'s manager-signature note still cites. Losing the race is
 * harmless: nothing was adopted, which is a state the next lines handle.
 */
const RUNTIME_CONFIG_RETRY_TIMEOUT_MS = 4000;

/**
 * How long a LOGIN DISPATCH will wait for the runtime config to be adopted
 * before it commits this browser to an identity provider.
 *
 * The same bound as {@link RUNTIME_CONFIG_RETRY_TIMEOUT_MS}, named separately
 * because it is a different decision: that one bounds a RETRY on the callback
 * path, this one bounds the FIRST fetch on the login path. Both exist for the
 * same reason — `requestJson` sets no timeout and no AbortSignal, so a backend
 * that accepts the connection and never answers would otherwise park the user
 * on a route that renders nothing at all, indefinitely.
 */
const RUNTIME_CONFIG_LOGIN_TIMEOUT_MS = RUNTIME_CONFIG_RETRY_TIMEOUT_MS;

/**
 * Await `inFlight`, but never longer than `timeoutMs`.
 *
 * Losing the race is not an error: it means nothing was adopted, which every
 * caller here already has a branch for. A rejection must not become the thing
 * that stops the caller either — the bound exists to guarantee the caller
 * RESUMES — but it is OBSERVED, never swallowed: today's only awaited promise
 * (`loadRuntimeAuthConfig`) is non-rejecting by contract, so the breadcrumb
 * marks the day a future caller hands this a promise that does reject.
 */
const settleOrTimeout = async (
  inFlight: Promise<unknown>,
  timeoutMs: number,
): Promise<void> => {
  await Promise.race([
    inFlight.catch((error: unknown) => {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message:
          'settleOrTimeout: awaited promise rejected; resuming under the bounded-wait contract',
        data: { error: String(error) },
      });
    }),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<CurrentUserProfile | null>(null);

  /**
   * Which adapter the session-event subscription below is bound to.
   *
   * <p>It exists because the two mount effects interleave: `boot()` is async
   * and suspends at its first `await`, so the subscription effect runs while
   * the runtime config is still in flight and would otherwise bind to whatever
   * the seam answered at that instant — and then never re-bind. On a Scalekit
   * deployment that meant subscribing to KEYCLOAK's session events for the life
   * of the page: no `onTokenExpired`, no `onLoggedOut` for the session the user
   * actually has.
   *
   * <p>Seeded from the pre-fetch answer (build-time flag, else keycloak), and
   * updated once the config lands. Naming it as state is what makes the
   * subscription a dependency of the answer instead of a race against it.
   */
  const [adapterProvider, setAdapterProvider] =
    useState<AuthProviderName>(resolveAuthProviderName);

  /**
   * The `loadRuntimeAuthConfig()` fetch `boot()` fired, so a login dispatch can
   * wait on the SAME request rather than starting a second one.
   *
   * Null only before `boot()` has run its first line. App.tsx holds the spinner
   * until `loading` clears, which is strictly after that, so no production
   * login path observes the null — the fallback below is belt and braces for a
   * caller mounted outside that ordering.
   */
  const runtimeConfigRef = useRef<Promise<void> | null>(null);

  const refreshProfile = async () => {
    if (__DEMO__) {
      const { demoUser } = await import('../demo/session');
      setAuthenticated(true);
      setUser(demoUser);
      return;
    }
    const token = await getAccessToken();
    if (!token) {
      setAuthenticated(false);
      setUser(null);
      return;
    }

    try {
      // Backend may omit `timezone` from the JSON when the user has no
      // preference set (NON_NULL serialization). Type the raw payload with
      // an optional timezone and normalise to `null` before storing in
      // state so consumers never have to disambiguate undefined vs null.
      const profile = await apiRequestJson<
        Omit<
          CurrentUserProfile,
          | 'timezone'
          | 'termsAcceptanceRequired'
          | 'requiredTermsBrandId'
          | 'requiredTermsVariantKey'
          | 'requiredTermsVersion'
        > & {
          timezone?: string | null;
          termsAcceptanceRequired?: boolean;
          requiredTermsBrandId?: string | null;
          requiredTermsVariantKey?: string | null;
          requiredTermsVersion?: string | null;
        }
      >('/api/auth/me');
      setAuthenticated(true);
      setUser({
        ...profile,
        timezone: profile.timezone ?? null,
        termsAcceptanceRequired: profile.termsAcceptanceRequired ?? false,
        requiredTermsBrandId: profile.requiredTermsBrandId ?? null,
        requiredTermsVariantKey: profile.requiredTermsVariantKey ?? null,
        requiredTermsVersion: profile.requiredTermsVersion ?? null,
        partnerEditorEnabled: profile.partnerEditorEnabled ?? false,
        reportingBetaEnabled: profile.reportingBetaEnabled ?? false,
      });
    } catch (err) {
      // Treat 429 (rate-limited) and transient network failures as recoverable:
      // keep the existing session, drop a breadcrumb, and schedule one
      // backoff retry so the UI self-heals instead of sitting indefinitely in
      // `authenticated=true, user=null`. Consumers already optional-chain on
      // `user`, so the interim render is harmless.
      if (isTransientAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'refreshProfile transient failure (retry scheduled)',
          data: {
            status: err instanceof ApiRequestError ? err.status : null,
          },
        });
        window.setTimeout(() => {
          void refreshProfile().catch(() => {});
        }, 2000);
        return;
      }
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        // Demo mode never starts Keycloak or Scalekit and never fetches
        // runtime auth config. The import is eliminated when __DEMO__ is false.
        if (__DEMO__) {
          const { demoUser } = await import('../demo/session');
          if (!cancelled) {
            setAuthenticated(true);
            setUser(demoUser);
          }
          return;
        }
        // THE RUNTIME LOOKUP MUST COST A PRODUCTION PAGE LOAD NOTHING (US-011).
        //
        // This fetch is fired here and awaited only in the one case that
        // genuinely cannot proceed without it. An unconditional await would put
        // a `GET /api/config/public` round trip in front of authentication on
        // every page load in every environment — `requestJson` sets no timeout,
        // so a hanging config endpoint would hang the boot spinner forever,
        // turning a soft dependency into a hard one on a path that has neither
        // today.
        //
        // WHEN THE ANSWER IS ACTUALLY NEEDED FIRST: a callback has to be
        // completed by the adapter that STARTED it, and handing a Scalekit
        // `?code=` to keycloak-js consumes it for nothing. So the condition is
        // "there is an authorization response in this URL AND the Scalekit
        // adapter is the one with a transaction outstanding". On a Keycloak
        // deployment the second half is never true — it is the only adapter
        // that leaves a transaction behind — so production never waits, not
        // even on the post-login redirect.
        //
        // WHY THE UNAWAITED CASE IS SAFE, per adapter, on a cold load:
        //   - keycloak: `check-sso`, exactly as it has always done. The runtime
        //     answer agrees with the build-time one on every deployment that
        //     has not advertised Scalekit, so there is nothing to be early for.
        //   - scalekit: `runBootstrap` probes with a `prompt=none` IFRAME and
        //     returns false on every failure answer — it deliberately never
        //     navigates on a cold load. So a bundle built for Scalekit, served
        //     by a backend that has rolled BACK to Keycloak, renders a login
        //     screen rather than bouncing the browser at the wrong issuer, and
        //     the config has landed long before the user clicks it. That is the
        //     direction AC-4's reversibility actually protects, and it needs no
        //     rebuild.
        //
        // Failures are swallowed inside `loadRuntimeAuthConfig`: with nothing
        // adopted the seam falls back to the build-time flag and then to
        // keycloak, which is what this SPA has always done.
        const runtimeConfig = loadRuntimeAuthConfig().then(() => {
          // Publish the resolved answer so the session-event subscription below
          // re-binds onto the adapter that actually owns the session.
          if (!cancelled) setAdapterProvider(resolveAuthProviderName());
        });
        // Published for the login-dispatch gate below, which must bound its
        // wait on THIS request rather than issuing a second one.
        runtimeConfigRef.current = runtimeConfig;

        // `/accept-invite` is the one cold-load route whose first session
        // bootstrap is itself a one-way provider choice. A keycloak-built
        // bundle calls keycloak-js `check-sso`; that leaves the dashboard for
        // the Keycloak issuer before the later re-dispatch can correct the
        // adapter. Staging exposed the result after a real invite was
        // delivered: the browser returned to this route with
        // `#error=login_required&iss=<keycloak>` even though the served config
        // said `login_provider=scalekit`.
        //
        // Pay the bounded config wait only on this route. Ordinary page loads
        // retain the zero-wait bootstrap contract above, while a wedged config
        // endpoint still falls through to the build-time adapter after the
        // same four-second ceiling used by explicit login dispatches.
        if (window.location.pathname === '/accept-invite' && !getRuntimeAuthConfig()) {
          await settleOrTimeout(runtimeConfig, RUNTIME_CONFIG_LOGIN_TIMEOUT_MS);
          if (!getRuntimeAuthConfig()) {
            Sentry.captureMessage(
              'auth.boot: runtime config unavailable on /accept-invite after the '
                + `bounded wait — bootstrapping the build-time adapter (${resolveAuthProviderName()})`,
              'warning',
            );
          }
        }
        if (hasAuthorizationResponse() && scalekitHasPendingTransaction()) {
          await runtimeConfig;
          // AWAITING THE FETCH IS NOT THE SAME AS GETTING AN ANSWER.
          // `loadRuntimeAuthConfig` swallows every failure and resolves void,
          // so this await returns with NOTHING ADOPTED when the endpoint 500s
          // — and `resolveAuthProviderName()` then falls back to the
          // build-time flag, which on a keycloak-built bundle is keycloak.
          // Handing a Scalekit `?code=` to keycloak-js burns a single-use
          // authorization code for nothing, strands the PKCE verifier and
          // replaceStates the URL, so the code cannot even be retried. The
          // build-time flag cannot answer this question: only the adapter that
          // STARTED the transaction can complete it, and the snapshot being
          // null is the difference between "adopted keycloak" and "adopted
          // nothing" that the provider name alone cannot express.
          if (!getRuntimeAuthConfig()) {
            // One bounded retry: a 500 on the config endpoint is usually
            // transient, and a reload is a worse experience than a second GET.
            //
            // BOUNDED IN TIME AS WELL AS IN COUNT. `requestJson` sets no
            // timeout and no AbortSignal, so a wedged backend that ACCEPTS and
            // never answers would hang this await forever — reintroducing on
            // the retry exactly the boot-spinner hang 5f4946622 removed from
            // the first fetch. The race caps the wait; losing it just means
            // nothing was adopted, which the next line already handles.
            await settleOrTimeout(
              loadRuntimeAuthConfig(),
              RUNTIME_CONFIG_RETRY_TIMEOUT_MS,
            );
            if (!cancelled) setAdapterProvider(resolveAuthProviderName());
          }
          // THE QUESTION IS NOT "DID AN ANSWER LAND", IT IS "DOES THE ANSWER
          // NAME THE ADAPTER THAT OWNS THIS CALLBACK". The branch's own
          // precondition already established that the SCALEKIT adapter started
          // this flow, so anything other than a served `scalekit` means the
          // code in this URL must not be handed to `startSession()`.
          const adopted = getRuntimeAuthConfig();
          if (!adopted) {
            // NOTHING ADOPTED. Fail closed with the callback INTACT: no
            // `startSession()`, so no adapter touches the URL — `?code=` and
            // `?state=` stay in window.location and the verifier stays in
            // sessionStorage, so a reload once the config endpoint recovers
            // completes this login for real.
            //
            // `cancelled` is repurposed exactly as the recovery-redirect path
            // below does it, to skip the `finally` and HOLD THE SPINNER. That
            // is load-bearing, not cosmetic: rendering the unauthenticated
            // state routes App.tsx's catch-all to `TriggerLogin`, whose effect
            // immediately calls `loginWithPassword` — which resolves the seam
            // through the build-time flag and navigates to the very adapter
            // this branch just refused to hand the callback to. There would be
            // no page left to reload.
            Sentry.captureMessage(
              'auth.boot: runtime config unavailable while completing a Scalekit ' +
                'callback — holding, refusing to dispatch it to the build-time adapter',
              'error',
            );
            cancelled = true;
            return;
          }
          if (adopted.loginProvider !== 'scalekit') {
            // THE ROLLBACK, CAUGHT MID-FLIGHT. An answer landed and it says
            // keycloak, while this browser is holding a Scalekit authorization
            // code — the AC-4 rollback happening between the authorize redirect
            // and the callback. The code is dead either way: the backend no
            // longer accepts tokens from the issuer that minted it. What must
            // NOT happen is feeding it to keycloak-js, which cannot match the
            // state and would burn it on an error.
            //
            // So discard it deliberately and continue to a normal keycloak
            // boot. Holding instead would stall forever: the transaction and
            // the `?code=` would both still be there on the next load, and the
            // guard would fire again with the same answer.
            Sentry.addBreadcrumb({
              category: 'auth',
              level: 'warning',
              message: 'boot: discarding a scalekit callback on a keycloak deployment',
            });
            discardScalekitTransaction();
            try {
              // Cosmetic, and deliberately not allowed to matter: discarding
              // the transaction is what stops the guard firing again, so a
              // `history.replaceState` this browser refuses must not be the
              // thing that stops a keycloak login from booting.
              cleanCurrentUrl();
            } catch {
              // Leave the dead params in the URL. They address nothing now.
            }
          }
        }
        // WHICH ADAPTER THIS BOOT DISPATCHED TO, AND WHY IT IS RE-CHECKED.
        //
        // On a cold load the config fetch above is NOT awaited, so
        // `startSession()` dispatches on the build-time flag. The plain
        // same-origin JSON GET routinely lands while that call is in flight —
        // it is far quicker than the cross-origin iframe probe the Scalekit
        // bootstrap does — and the runtime answer wins outright, so every line
        // AFTER `startSession()` (`getSubject`, `refreshProfile` ->
        // `getAccessToken`) would otherwise dispatch to an adapter that was
        // never bootstrapped, and answer "no session" for a user who has one.
        // During the mixed-build window that is a login bounce on every cold
        // load. Bootstrapping the adapter the answer actually names is the
        // whole fix; the loop is bounded because a config is adopted once per
        // page load, so a second pass is belt and braces rather than a wait.
        let bootProvider = resolveAuthProviderName();
        let isAuthenticated = await startSession();
        for (
          let pass = 0;
          pass < 2 && !cancelled && resolveAuthProviderName() !== bootProvider;
          pass += 1
        ) {
          bootProvider = resolveAuthProviderName();
          isAuthenticated = await startSession();
        }
        // The adapter has now processed any OAuth callback and called
        // history.replaceState to clean the URL. It is safe to start Session
        // Replay — its internal URL capture will no longer see the OAuth
        // code / state in window.location.
        enableSentryReplay();
        if (cancelled) return;
        setAuthenticated(isAuthenticated);
        if (isAuthenticated) {
          setSentryUser(getSubject());
          await refreshProfile();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (isRecoveryRedirectPending(err)) {
          // The adapter has issued a full-page navigation to the identity
          // provider. Keep the spinner on screen — we deliberately repurpose
          // the `cancelled` flag to skip the `finally` branch below so the
          // user never sees the unauthenticated TriggerLogin flash.
          cancelled = true;
          return;
        }
        if (isTransientAuthError(err)) {
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'warning',
            message: 'boot transient auth error (ignored)',
            data: { message },
          });
        } else {
          Sentry.captureException(err);
        }
        if (!cancelled) {
          setAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (__DEMO__) return;
    return subscribeToSessionEvents({
    onAuthenticated: () => {
      setSentryUser(getSubject());
      void refreshProfile();
    },
    onLoggedOut: () => {
      setSentryUser(undefined);
      setAuthenticated(false);
      setUser(null);
    },
    onTokenExpired: () => {
      void getAccessToken();
    },
    // Mid-session auth-flow errors (e.g. PKCE/state mismatch on a silent
    // token refresh) hit recoverSession() rather than startSession() — the
    // latter memoizes the already-successful bootstrap and would be a no-op
    // after a session is established. recoverSession() resets the state
    // machine and hits the recovery ladder, which navigates to a fresh
    // authorize URL. Swallow the rejection — recovery either navigates
    // away or trips the loop guard (surfaced via onLoggedOut).
    onAuthError: (err) => {
      // keycloak-js sometimes invokes this with `undefined` when a fetch
      // rejects mid-callback (the real failure is already captured by the
      // bootstrap catch). Drop a breadcrumb for those instead of adding a
      // second `Error: undefined` issue with no stacktrace.
      if (err) {
        Sentry.captureException(err, { tags: { source: 'auth.onAuthError' } });
      } else {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'auth.onAuthError (no payload)',
        });
      }
      void recoverSession().catch(() => {});
    },
    onRefreshError: () => {
      Sentry.addBreadcrumb({ category: 'auth', message: 'onAuthRefreshError' });
      void recoverSession().catch(() => {});
    },
    // Re-bind when the resolved adapter changes. The cleanup this effect
    // returns unsubscribes from the previous adapter first, so the two never
    // hold the session-event hooks at once.
    });
  }, [adapterProvider]);

  /**
   * WAIT FOR THE RUNTIME ANSWER BEFORE COMMITTING TO AN IDENTITY PROVIDER.
   *
   * A login dispatch is not like the cold-load `startSession()` in `boot()`: it
   * ends in a FULL-PAGE NAVIGATION to whichever provider the seam names at that
   * instant, and there is no second pass after a navigation. So the bounded
   * re-dispatch loop that makes an early `startSession()` harmless cannot make
   * an early `beginLogin()` harmless — by then the page is gone.
   *
   * That gap is a live defect rather than a hypothetical. App.tsx routes every
   * unauthenticated route to `TriggerLogin`, whose effect fires the moment
   * `loading` clears — and `loading` clears as soon as `startSession()` returns,
   * which on a cold load routinely beats the config GET. A fresh browser on a
   * deployment serving `login_provider=scalekit` was therefore dispatched to
   * KEYCLOAK, with the runtime answer landing moments later into a page that had
   * already navigated away.
   *
   * THE CALLBACK BRANCH IN `boot()` ALREADY SOLVES THIS EXACT PROBLEM — a
   * bounded `Promise.race` on the config fetch plus a Sentry capture when
   * nothing was adopted — because handing a callback to the wrong adapter is
   * likewise unrecoverable. This extends the same discipline to the other
   * one-way door.
   *
   * BOUNDED, AND THE BUILD-TIME FALLBACK SURVIVES. An adopted config dispatches
   * with no wait whatsoever. Otherwise the wait is capped, and when it ends with
   * nothing adopted the dispatch happens ANYWAY, on the build-time flag — the
   * answer this SPA has always given. Never blanking the page is the point: the
   * bound decides WHEN the commitment is made, never WHETHER.
   */
  const commitLoginRedirect = (
    dispatch: (redirectPath?: string) => Promise<void>,
  ) => async (redirectPath?: string): Promise<void> => {
    if (__DEMO__) return;
    if (!getRuntimeAuthConfig()) {
      const inFlight = runtimeConfigRef.current ?? loadRuntimeAuthConfig();
      runtimeConfigRef.current = inFlight;
      await settleOrTimeout(inFlight, RUNTIME_CONFIG_LOGIN_TIMEOUT_MS);
      if (!getRuntimeAuthConfig()) {
        // Degraded, not stopped — hence `warning` where the callback branch
        // captures `error`: that branch HOLDS the page, this one proceeds. It
        // is worth an event rather than only a breadcrumb because the failure
        // it names is otherwise invisible from the outside: the visitor reaches
        // a perfectly working sign-in page at the wrong provider, and nothing
        // else about the boot reports anything at all.
        Sentry.captureMessage(
          'auth.login: runtime config unavailable after the bounded wait — ' +
            'dispatching the login redirect to the build-time adapter ' +
            `(${resolveAuthProviderName()})`,
          'warning',
        );
      }
    }
    // Resolved HERE, after the wait: the seam reads the provider at call time,
    // so this is the instant the browser is committed to one.
    await dispatch(redirectPath);
  };

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    authenticated,
    user,
    loginWithGoogle: commitLoginRedirect(beginFederatedLogin),
    loginWithPassword: commitLoginRedirect(beginLogin),
    logout: async () => {
      if (__DEMO__) {
        setAuthenticated(false);
        setUser(null);
        return;
      }
      setSentryUser(undefined);
      await endSession();
    },
    refreshProfile,
    getAccessToken: () => (__DEMO__ ? Promise.resolve(null) : getAccessToken()),
  }), [loading, authenticated, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Non-throwing variant of {@link useAuth} for components that must render
 * outside an {@link AuthProvider} (e.g. isolated component tests that don't
 * stand up the full provider stack). Returns `null` when no provider is
 * mounted; callers treat that as "identity unknown" and degrade gracefully
 * (the server remains the true authority regardless of what the UI shows).
 */
export const useAuthOptional = (): AuthContextValue | null =>
  useContext(AuthContext);
